import { apiSource } from "./api-source";
import { compose, isLive, liveAreas } from "./areas";
import { dataChanged } from "./changes";
import { stubSource } from "./stubs";
import type { DataSource } from "./source";

/**
 * Per area rather than one switch. VITE_LIVE_AREAS lists the parts of the app
 * that read the real API; everything else stays on fixtures.
 *
 * The areas fill up at different times. Clients are typed in by a manager, so
 * that table has rows as soon as somebody adds one. Productions and roles have
 * nothing until the first extraction run, and taking them live early means an
 * empty directory where there used to be a working one.
 */
const source: DataSource = compose(apiSource, stubSource);

/**
 * The source, with writes announcing themselves.
 *
 * Wrapped here rather than inside each implementation so both get it and
 * neither has to remember. A screen that logs a call should not also have to
 * know that the dashboard feed and the bell are showing a stale list.
 */
export const data: DataSource = {
  ...source,
  addClient: async (client) => {
    const created = await source.addClient(client);
    dataChanged();
    return created;
  },
  updateClient: async (id, client) => {
    const updated = await source.updateClient(id, client);
    dataChanged();
    return updated;
  },
  deleteClient: async (id) => {
    await source.deleteClient(id);
    dataChanged();
  },
  addCall: async (entry) => {
    const created = await source.addCall(entry);
    dataChanged();
    return created;
  },
  applyProjectChange: async (change) => {
    await source.applyProjectChange(change);
    dataChanged();
  },
  setTracked: async (clientId, roleId, tracked) => {
    await source.setTracked(clientId, roleId, tracked);
    dataChanged();
  },
  setSeen: async (clientId, projectId, seen) => {
    await source.setSeen(clientId, projectId, seen);
    dataChanged();
  },
  markListSeen: async (clientId, seen, q) => {
    const summary = await source.markListSeen(clientId, seen, q);
    dataChanged();
    return summary;
  },
};

export { isLive, liveAreas };
export type { Area } from "./areas";
export { onDataChange } from "./changes";
export type { DataSource };
